/**
 * AAP 2022 hyperbilirubinemia thresholds — data access layer.
 *
 * The numbers live in `aap2022-thresholds.json` (generated by
 * `../data/new-graphs-2026/build-thresholds.mjs`, provenance in
 * `THRESHOLDS_AAP2022.md`). This module only looks them up: no clinical
 * decisions, no unit conversion, no direct-bilirubin subtraction. Everything
 * here is mg/dL, exactly as transcribed (one decimal), interpolated linearly
 * between integer hours.
 *
 * Guideline rules encoded here (Kemper et al., Pediatrics 2022):
 *  - GA >= 40 uses the 40-week curve; GA 35-39 uses the completed week.
 *  - GA < 35 is out of scope -> throws. Never returns a value.
 *  - Age < 1 h -> hour-1 value. Age > 336 h -> hour-336 value, flagged.
 *
 * There are TWO published curves, not three. The escalation-of-care line is a
 * fixed distance below the exchange curve (procedure §10 / AAP 2022: 2.0 mg/dL)
 * and that distance is a hospital setting, so it is derived in `aapCalculation.ts`
 * from `escalationDeltaMgDl` and never stored here.
 */
import thresholdsJson from './aap2022-thresholds.json';

export type AapRisk = 'no_risk' | 'with_risk';
export type AapThresholdKind = 'phototherapy' | 'exchange';

interface CurveSet {
  phototherapy: number[];
  exchange: number[];
}

/** One hour-point where the two transcriptions of the AAP tables disagreed. */
export interface AapDiscrepancyPoint {
  threshold: AapThresholdKind;
  gestationalAgeWeeks: number;
  risk: AapRisk;
  ageHours: number;
  /** The value in use: PediTools' answer when validated, else the lower transcription. */
  value: number;
  /** The transcription value that is not in use. */
  alternate: number;
  sourceA: number;
  sourceB: number;
  /** True once the point was checked against PediTools bili2022 (then `peditools` holds its answer). */
  validated: boolean;
  peditools?: number;
  /** Present when the point was mirrored from GA 38 onto a shared >=38 curve. */
  propagatedFrom?: number;
}

/** @deprecated use AapDiscrepancyPoint */
export type AapNeedsValidationPoint = AapDiscrepancyPoint;

interface ThresholdsFile {
  meta: {
    source: string;
    units: string;
    ageHours: { min: number; max: number };
    gestationalAgeWeeks: number[];
    risk: AapRisk[];
  };
  curves: Record<AapRisk, Record<string, CurveSet>>;
  discrepancies: AapDiscrepancyPoint[];
  needsValidation: AapDiscrepancyPoint[];
}

const data = thresholdsJson as unknown as ThresholdsFile;

export const AAP_MIN_GA_WEEKS = 35;
export const AAP_MAX_GA_WEEKS = 40;
export const AAP_MIN_HOURS: number = data.meta.ageHours.min; // 1
export const AAP_MAX_HOURS: number = data.meta.ageHours.max; // 336
export const AAP_GA_WEEKS: readonly number[] = data.meta.gestationalAgeWeeks;
/** Every transcription discrepancy, with how it was resolved. */
export const AAP_DISCREPANCIES: readonly AapDiscrepancyPoint[] = data.discrepancies;
/** The subset of discrepancies not yet confirmed against PediTools (lower value in use). */
export const AAP_NEEDS_VALIDATION: readonly AapDiscrepancyPoint[] = data.needsValidation;
export const AAP_DATA_SOURCE: string = data.meta.source;

export class AapOutOfScopeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AapOutOfScopeError';
  }
}

export interface AapThresholds {
  phototherapy: number;
  exchange: number;
  /** The curve actually read: 35..40 (40 also serves GA > 40). */
  gestationalAgeWeeksUsed: number;
  /** The age the values were read at, after clamping to [1, 336]. */
  ageHoursUsed: number;
  /** True when the requested age was above 336 h (values are the hour-336 plateau). */
  outOfGuidelineRange: boolean;
  /** True when any hour used for the lookup is a discrepancy point not yet confirmed against PediTools. */
  needsValidation: boolean;
}

export const riskKey = (hasNeurotoxicityRisk: boolean): AapRisk =>
  hasNeurotoxicityRisk ? 'with_risk' : 'no_risk';

/**
 * Maps a gestational age (weeks, may carry a fraction for days) to the curve
 * that serves it. Throws for GA < 35 or non-finite input.
 */
export function resolveCurveWeek(gestationalAgeWeeks: number): number {
  if (!Number.isFinite(gestationalAgeWeeks)) {
    throw new AapOutOfScopeError(`Gestational age must be a number, got ${gestationalAgeWeeks}`);
  }
  const completedWeek = Math.floor(gestationalAgeWeeks);
  if (completedWeek < AAP_MIN_GA_WEEKS) {
    throw new AapOutOfScopeError(
      `AAP 2022 thresholds cover 35 weeks and above; got ${gestationalAgeWeeks} weeks`
    );
  }
  return Math.min(completedWeek, AAP_MAX_GA_WEEKS);
}

/** Raw hourly curve (index 0 = hour 1). Returns the shared array; do not mutate. */
export function getAapCurve(
  gestationalAgeWeeks: number,
  hasNeurotoxicityRisk: boolean,
  kind: AapThresholdKind
): readonly number[] {
  const week = resolveCurveWeek(gestationalAgeWeeks);
  return data.curves[riskKey(hasNeurotoxicityRisk)][String(week)][kind];
}

const flaggedIndex = new Set(
  data.needsValidation.map((p) => `${p.threshold}:${p.gestationalAgeWeeks}:${p.risk}:${p.ageHours}`)
);

const isFlagged = (week: number, risk: AapRisk, hour: number): boolean =>
  flaggedIndex.has(`phototherapy:${week}:${risk}:${hour}`) ||
  flaggedIndex.has(`exchange:${week}:${risk}:${hour}`);

const interpolate = (curve: readonly number[], lo: number, hi: number, t: number): number => {
  const y0 = curve[lo - 1];
  const y1 = curve[hi - 1];
  return t === 0 ? y0 : y0 + (y1 - y0) * t;
};

/**
 * The single entry point for threshold lookups.
 *
 * @param gestationalAgeWeeks 35 and above (fractions allowed; the completed week is used)
 * @param ageHours            postnatal age in hours; clamped to [1, 336]
 * @param hasNeurotoxicityRisk any neurotoxicity risk factor other than gestational age
 */
export function getAapThresholds(
  gestationalAgeWeeks: number,
  ageHours: number,
  hasNeurotoxicityRisk: boolean
): AapThresholds {
  const week = resolveCurveWeek(gestationalAgeWeeks);
  if (!Number.isFinite(ageHours) || ageHours < 0) {
    throw new AapOutOfScopeError(`Age in hours must be a non-negative number, got ${ageHours}`);
  }
  const risk = riskKey(hasNeurotoxicityRisk);
  const curves = data.curves[risk][String(week)];

  const outOfGuidelineRange = ageHours > AAP_MAX_HOURS;
  const age = Math.min(Math.max(ageHours, AAP_MIN_HOURS), AAP_MAX_HOURS);
  const lo = Math.floor(age);
  const hi = Math.min(Math.ceil(age), AAP_MAX_HOURS);
  const t = hi === lo ? 0 : age - lo;

  return {
    phototherapy: interpolate(curves.phototherapy, lo, hi, t),
    exchange: interpolate(curves.exchange, lo, hi, t),
    gestationalAgeWeeksUsed: week,
    ageHoursUsed: age,
    outOfGuidelineRange,
    needsValidation: isFlagged(week, risk, lo) || (t > 0 && isFlagged(week, risk, hi)),
  };
}
